import { useEffect, useState } from 'react';
import { execApi } from '../services/api';
import { useAuth } from './useAuth';

// What this user may open, resolved by the backend from their role + per-dashboard
// grants. One fetch on load, shared by the sidebar and the route guards so the two
// can never disagree about what exists.
//
// This is presentation only. It decides what is easy to find, not what is possible —
// every endpoint checks the same grants for itself, because anything the browser is
// told, the browser can be made to lie about.

export type Level = 'view' | 'edit';

export interface Panel {
  id: string;
  name: string;
  route: string;
  /** One-line description of what the board answers — drawn on the hub cards. */
  purpose?: string;
  level: Level;
}

export interface Permissions {
  loading: boolean;
  role: string;
  /** dashboard id → level. Absent means no access. */
  dashboards: Record<string, Level>;
  panels: Panel[];
  can: (dashboardId: string, level?: Level) => boolean;
}

export function usePermissions(): Permissions {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<{ role: string; dashboards: Record<string, Level>; panels: Panel[] }>(
    { role: '', dashboards: {}, panels: [] },
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    let alive = true;
    execApi.getMyDashboards()
      .then((res) => {
        if (!alive) return;
        const d = res.data as { role: string; dashboards: Record<string, Level>; panels: Panel[] };
        setState({ role: d.role, dashboards: d.dashboards || {}, panels: d.panels || [] });
      })
      // Fail closed: if we cannot establish what someone may see, show nothing
      // rather than guessing generously.
      .catch(() => { if (alive) setState({ role: '', dashboards: {}, panels: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user, authLoading]);

  // Live reorder (Ohad 2026-08-15): when the hub saves a new card order it
  // announces it, and every mounted instance of this hook — the sidebar,
  // most importantly — re-sorts on the spot instead of waiting for a refresh.
  useEffect(() => {
    const onOrder = (e: Event) => {
      const order = (e as CustomEvent<string[]>).detail;
      if (!Array.isArray(order)) return;
      setState((prev) => {
        const rank: Record<string, number> = {};
        order.forEach((id, i) => { rank[id] = i; });
        const panels = [...prev.panels].sort(
          (a, b) => (rank[a.id] ?? 999) - (rank[b.id] ?? 999),
        );
        return { ...prev, panels };
      });
    };
    window.addEventListener('dashboards:order', onOrder);
    return () => window.removeEventListener('dashboards:order', onOrder);
  }, []);

  const can = (dashboardId: string, level: Level = 'view') => {
    const held = state.dashboards[dashboardId];
    if (!held) return false;
    return level === 'view' ? true : held === 'edit';
  };

  return { loading: loading || authLoading, ...state, can };
}
